import { useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound } from "common/utils";
import { PieceStatus } from "features/builder/types";
import { MockPiece, PieceListContext, PieceCardAction } from "features/builder/pieceList";

const FALLBACK_IMAGE = `${Constants.assetHost}/assets/images/default_capture.png`;

const STATUS_LABEL_KEY: Partial<Record<PieceStatus, string>> = {
  [PieceStatus.DRAFT_NEW]: "MSG_BUILDER_CARD_STATUS_DRAFT",
  [PieceStatus.EDITING]: "MSG_BUILDER_CARD_STATUS_EDITING",
  [PieceStatus.UNPUBLISHED]: "MSG_BUILDER_CARD_STATUS_UNPUBLISHED",
  [PieceStatus.REMOVED]: "MSG_BUILDER_CARD_STATUS_REMOVED",
};

enum DetailTab {
  INFO = "info",
  RANKING = "ranking",
  HINT = "hint",
}

const TAB_ITEMS: { id: DetailTab; labelKey: string; icon: string }[] = [
  { id: DetailTab.INFO, labelKey: "MSG_BUILDER_MAP_DETAIL_BTN", icon: "info" },
  { id: DetailTab.RANKING, labelKey: "MSG_BUILDER_DETAIL_TAB_RANKING", icon: "leaderboard" },
  { id: DetailTab.HINT, labelKey: "MSG_BUILDER_MENU_HINT", icon: "edit_note" },
];

// UI 確認用ダミーランキング（B1 は後続フェーズ。表示構造のみ）
const DUMMY_RANKING = [
  { rank: 1, medal: "👑", name: "アヤカ", moves: 12, color: "bg-orange-300" },
  { rank: 2, medal: "🥈", name: "ハル", moves: 14, color: "bg-blue-300" },
  { rank: 3, medal: "🥉", name: "ナナミ", moves: 15, color: "bg-green-300" },
];
const HINT_LEVELS = [
  "MSG_BUILDER_DETAIL_HINT_LV1",
  "MSG_BUILDER_DETAIL_HINT_LV2",
  "MSG_BUILDER_DETAIL_HINT_LV3",
];

export const MapDetailPage = ({
  piece,
  context,
  onBack,
  onAction,
}: {
  piece: MockPiece;
  context: PieceListContext;
  onBack: () => void;
  onAction: (action: PieceCardAction) => void;
}) => {
  const play = usePlaySound();
  const [tab, setTab] = useState<DetailTab>(DetailTab.INFO);
  const isOfficial = piece.is_template;
  const statusLabelKey = isOfficial
    ? "MSG_BUILDER_CARD_STATUS_OFFICIAL"
    : STATUS_LABEL_KEY[piece.status];
  const tags = piece.tags
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  const accentBg =
    context === PieceListContext.MINE
      ? "bg-green-100"
      : context === PieceListContext.TRASH
        ? "bg-brown-80"
        : "bg-blue-100";
  const accentBorder =
    context === PieceListContext.MINE
      ? "border-green-100"
      : context === PieceListContext.TRASH
        ? "border-brown-80"
        : "border-blue-100";
  const accentSoftBg =
    context === PieceListContext.MINE
      ? "bg-green-100/30"
      : context === PieceListContext.TRASH
        ? "bg-brown-80/30"
        : "bg-blue-100/30";
  const accentDescBg =
    context === PieceListContext.MINE
      ? "bg-green-100/20"
      : context === PieceListContext.TRASH
        ? "bg-brown-80/20"
        : "bg-blue-100/20";

  const fire = (action: PieceCardAction) => {
    play();
    btnDelay(() => onAction(action));
  };

  const handleBack = () => {
    play();
    btnDelay(() => onBack());
  };

  const handleSelectTab = (id: DetailTab) => () => {
    if (id === tab) return;
    play();
    btnDelay(() => setTab(id));
  };

  const handleCopyCode = () => {
    play();
    toast(I18n.t("MSG_BUILDER_COPY_TOAST"));
  };

  return (
    <div className="flex-col-el w-full h-full p-4 gap-3">
      {/* 上部バー */}
      <div className="flex-row-el items-center justify-between w-full shrink-0 upsilon">
        <button
          type="button"
          onClick={handleBack}
          aria-label="back"
          className="type-inherit-font btn btn-blue"
        >
          <div className="flex-row-center gap-1">
            <span className="material-symbols-outlined text-white type-sm">arrow_back</span>
            <p className="type-text type-sm text-white whitespace-nowrap">
              {I18n.t("MSG_BACK_BTN")}
            </p>
          </div>
        </button>

        {context === PieceListContext.MINE && piece.status !== PieceStatus.DRAFT_NEW && (
          <button
            type="button"
            onClick={() => fire(PieceCardAction.TO_TRASH)}
            aria-label="to-trash"
            className="type-inherit-font btn btn-danger"
          >
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-sm">delete</span>
              <p className="type-text type-sm text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_MENU_TO_TRASH")}
              </p>
            </div>
          </button>
        )}
      </div>

      {/* 中央：左（画像＋アプリコード）＋ 右（タブ＋パネル） */}
      <div className="flex-row-el flex-1 w-full gap-4 min-h-0">
        <div className="flex-col-el w-[360px] shrink-0 gap-3">
          <div
            className={`flex-col-el w-full h-[260px] rounded-2xl overflow-hidden bg-beige-60 border-solid border-[4px] shadow-md ${
              isOfficial ? "border-blue-100 shadow-[0_0_12px_rgba(76,157,241,0.4)]" : accentBorder
            }`}
          >
            {piece.cover || piece.capture ? (
              <img
                alt={piece.name}
                src={piece.cover ?? piece.capture ?? FALLBACK_IMAGE}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                }}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex-col-center w-full h-full">
                <p className="type-text type-display">{piece.emoji ?? "🧩"}</p>
              </div>
            )}
          </div>

          <div className="flex-row-el items-center justify-between w-full gap-2 bg-white rounded-xl px-3 py-2 shadow-md">
            <div className="flex-col-el items-start min-w-0">
              <p className="type-text type-sm text-textcolor-black/60 whitespace-nowrap">
                {I18n.t("MSG_BUILDER_DETAIL_APP_CODE")}
              </p>
              <p className="type-text type-md text-textcolor-black w-full text-left truncate">
                {piece.inquiry_id}
              </p>
            </div>
            <div className="upsilon shrink-0">
              <button
                type="button"
                onClick={handleCopyCode}
                aria-label="copy-code"
                className="type-inherit-font btn btn-blue !p-2"
              >
                <span className="material-symbols-outlined text-white type-sm">content_copy</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-col-el flex-1 min-w-0 gap-3">
          {/* タブバー */}
          <div className="flex-row-el items-center gap-2 shrink-0">
            {TAB_ITEMS.map(({ id, labelKey, icon }) => {
              const active = id === tab;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={handleSelectTab(id)}
                  className={`type-inherit-font flex-row-center shrink-0 gap-1 px-4 py-2 rounded-full border-solid border-2 cursor-pointer ${
                    active ? `${accentBg} ${accentBorder}` : `bg-white ${accentBorder}`
                  }`}
                >
                  <span
                    className={`material-symbols-outlined type-sm ${
                      active ? "text-white" : "text-textcolor-black"
                    }`}
                  >
                    {icon}
                  </span>
                  <p
                    className={`type-text type-sm whitespace-nowrap ${
                      active ? "text-white" : "text-textcolor-black"
                    }`}
                  >
                    {I18n.t(labelKey)}
                  </p>
                </button>
              );
            })}
          </div>

          {/* パネル */}
          <div className="flex-col-el items-start flex-1 min-h-0 w-full gap-2 bg-white rounded-2xl p-4 shadow-md overflow-y-auto invisible-scrollbar">
            {tab === DetailTab.INFO && (
              <>
                <div className="flex-row-el items-center gap-2">
                  <p className="type-text type-title text-textcolor-black text-left">
                    {piece.name}
                  </p>
                  {statusLabelKey && (
                    <div className="flex-row-center px-3 py-1 rounded-full bg-gray2-100 shrink-0">
                      <p className="type-text type-sm text-white whitespace-nowrap">
                        {I18n.t(statusLabelKey)}
                      </p>
                    </div>
                  )}
                </div>

                {piece.author_name && (
                  <p className="type-text type-sm text-textcolor-black/70 w-full text-left truncate">
                    {piece.original_author_name
                      ? `${piece.author_name}  ←  ${I18n.t("MSG_BUILDER_CARD_REMIX_FROM", {
                          name: piece.original_author_name,
                        })}`
                      : I18n.t("MSG_BUILDER_DETAIL_AUTHOR_BY", { name: piece.author_name })}
                  </p>
                )}

                <div className="flex-row-el items-center gap-4">
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-[#4c9df1] type-sm">
                      visibility
                    </span>
                    <p className="type-text type-sm text-textcolor-black">{piece.viewed}</p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined material-symbols-filled text-[#fc6560] type-sm">
                      favorite
                    </span>
                    <p className="type-text type-sm text-textcolor-black">{piece.liked}</p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-green-100 type-sm">
                      play_arrow
                    </span>
                    <p className="type-text type-sm text-textcolor-black">{piece.played}</p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-[#2bdc98] type-sm">
                      military_tech
                    </span>
                    <p className="type-text type-sm text-textcolor-black">{piece.cleared}</p>
                  </div>
                </div>

                <p className="type-text type-sm text-textcolor-black/60 w-full text-left">
                  {I18n.t("MSG_BUILDER_DETAIL_PUBLISHED_AT")}{" "}
                  {piece.published_at ? dayjs(piece.published_at).format("YYYY-MM-DD") : "-"}
                  {"  /  "}
                  {I18n.t("MSG_BUILDER_DETAIL_UPDATED_AT")}{" "}
                  {dayjs(piece.updated_at).format("YYYY-MM-DD")}
                  {`  /  v${piece.version}`}
                </p>

                {tags.length > 0 && (
                  <div className="flex-row-el flex-wrap items-center gap-2">
                    {tags.map((t) => (
                      <div
                        key={t}
                        className={`flex-row-center px-3 py-1 rounded-full ${accentSoftBg}`}
                      >
                        <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                          {t}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {piece.description && (
                  <div className={`flex-col-el items-start w-full rounded-xl p-3 ${accentDescBg}`}>
                    <p className="type-text type-sm text-textcolor-black w-full text-left">
                      {piece.description}
                    </p>
                  </div>
                )}
              </>
            )}

            {tab === DetailTab.RANKING && (
              <>
                <div className="flex-row-el items-center justify-between w-full rounded-xl px-4 py-3 bg-yellow-100">
                  <div className="flex-col-el items-start">
                    <p className="type-text type-sm text-textcolor-black/70">
                      {I18n.t("MSG_BUILDER_DETAIL_RANKING_WORLD")}
                    </p>
                    <p className="type-text type-title text-textcolor-black">
                      {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", { n: 12 })}
                    </p>
                  </div>
                  <p className="type-text type-title">👑</p>
                </div>

                <div className="flex-col-el w-full gap-2">
                  {DUMMY_RANKING.map((r) => (
                    <div
                      key={r.rank}
                      className="flex-row-el items-center gap-2 w-full rounded-xl px-3 py-2 bg-beige-60"
                    >
                      <p className="type-text type-sm">{r.medal}</p>
                      <p className="type-text type-sm text-textcolor-black w-6 text-center">
                        {r.rank}
                      </p>
                      <div className={`flex-col-center w-8 h-8 rounded-full ${r.color}`}>
                        <p className="type-text type-sm text-white">{r.name.slice(0, 1)}</p>
                      </div>
                      <p className="type-text type-sm text-textcolor-black flex-1 text-left truncate">
                        {r.name}
                      </p>
                      <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                        {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", { n: r.moves })}
                      </p>
                    </div>
                  ))}

                  <div className="flex-row-el items-center gap-2 w-full rounded-xl px-3 py-2 border-solid border-2 border-yellow-100 bg-yellow-100/30">
                    <p className="type-text type-sm text-textcolor-black w-6 text-center">11</p>
                    <div className={`flex-col-center w-8 h-8 rounded-full ${accentBg}`}>
                      <p className="type-text type-sm text-white">
                        {I18n.t("MSG_BUILDER_DETAIL_RANKING_YOU").slice(0, 1)}
                      </p>
                    </div>
                    <p className="type-text type-sm text-textcolor-black flex-1 text-left">
                      {I18n.t("MSG_BUILDER_DETAIL_RANKING_YOU")}
                    </p>
                    <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                      {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", { n: 20 })}
                    </p>
                  </div>
                </div>
              </>
            )}

            {tab === DetailTab.HINT && (
              <>
                <p className="type-text type-md text-textcolor-black w-full text-left">
                  {I18n.t("MSG_BUILDER_DETAIL_HINT_TITLE")}
                </p>
                <p className="type-text type-sm text-textcolor-black/60 w-full text-left">
                  {I18n.t("MSG_BUILDER_DETAIL_HINT_DESC")}
                </p>
                <div className="flex-col-el w-full gap-3">
                  {HINT_LEVELS.map((lv) => (
                    <div key={lv} className="flex-col-el items-start w-full gap-1">
                      <p className="type-text type-sm text-textcolor-black w-full text-left">
                        {I18n.t(lv)}
                      </p>
                      <textarea
                        className="type-text type-sm text-textcolor-black w-full rounded-xl p-3 border-solid border-2 border-gray2-30 outline-none resize-none"
                        rows={2}
                        placeholder={I18n.t("MSG_BUILDER_DETAIL_HINT_PLACEHOLDER")}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 下部アクションバー */}
      <div className="flex-row-el justify-center items-center w-full gap-3 shrink-0 upsilon">
        <button
          type="button"
          onClick={() => fire(PieceCardAction.PLAY)}
          aria-label="play"
          className="type-inherit-font btn btn-primary"
        >
          <div className="flex-row-center gap-2">
            <span className="material-symbols-outlined text-white type-md">play_arrow</span>
            <p className="type-text type-body text-white whitespace-nowrap">
              {I18n.t("MSG_BUILDER_BTN_PLAY")}
            </p>
          </div>
        </button>

        {context === PieceListContext.MINE && (
          <button
            type="button"
            onClick={() => fire(PieceCardAction.EDIT)}
            aria-label="edit"
            className="type-inherit-font btn btn-blue"
          >
            <div className="flex-row-center gap-2">
              <span className="material-symbols-outlined text-white type-md">edit</span>
              <p className="type-text type-body text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_MAP_EDIT_BTN")}
              </p>
            </div>
          </button>
        )}

        <button
          type="button"
          onClick={() => fire(PieceCardAction.COPY)}
          aria-label="copy"
          className="type-inherit-font btn btn-purple"
        >
          <div className="flex-row-center gap-2">
            <span className="material-symbols-outlined text-white type-md">cached</span>
            <p className="type-text type-body text-white whitespace-nowrap">
              {I18n.t("MSG_BUILDER_MENU_COPY")}
            </p>
          </div>
        </button>
      </div>
    </div>
  );
};
